import { Link } from "react-router";
import type { HygieneQuestion, Option } from "../../api/content";
import type { FinanceView } from "../../api/finance";
import { hygieneAnswers } from "../../api/finance";
import type { StepGate } from "../../api/types";
import { Banner } from "../../components/Banner";
import { GateFooter } from "../../components/GateFooter";
import { Guide } from "../../components/Guide";
import { OptionTile } from "../../components/OptionTile";
import { Pill } from "../../components/Pill";
import { ScreenHeader } from "../../components/ScreenHeader";
import { Section } from "../../components/Section";
import { BackButton } from "../../components/BackButton";

/*
 * Access to Finance: preparation checklists only. Nothing here collects ID numbers, documents or financial figures;
 * the provider collects and verifies everything directly when the participant is referred.
 */

const NO_DATA = (
  <Banner tone="warn">
    <b>Don't type or upload any ID numbers, documents or bank details here.</b> We only need to know
    you're prepared. Your provider collects and verifies everything directly when you're referred.
  </Banner>
);

interface StepProps {
  view: FinanceView;
  busy: boolean;
  done: boolean;
  onComplete: () => void;
}

function gateOf(view: FinanceView, step: number): StepGate {
  return view.gates[step - 1] ?? { pass: false, items: [] };
}

function toggle(list: string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
}

export function FinServicesScreen({
  view,
  busy,
  done,
  services,
  onChange,
  onComplete,
}: StepProps & {
  services: Option[];
  onChange: (keys: string[]) => void;
}) {
  const picked = view.record.services;
  return (
    <div>
      <ScreenHeader
        kicker="Finance Step 1 · Express Interest"
        title="Which financial service are you interested in?"
        sub="Access to Finance runs alongside your gig steps. Choose every service you'd like to be considered for. You can change this later."
      />
      <Guide guideKey="fin1" done={done} />
      <div className="flex flex-col gap-2">
        {services.map((service, index) => (
          <OptionTile
            key={service.k}
            marker={index + 1}
            selected={picked.includes(service.k)}
            title={service.t}
            detail={service.d}
            onToggle={() => onChange(toggle(picked, service.k))}
          />
        ))}
      </div>
      <p className="mt-2.5 text-[13px] text-text-muted">
        {picked.length ? `${picked.length} selected` : "Choose at least one to continue."}
      </p>
      <GateFooter
        gate={gateOf(view, 1)}
        done={done}
        label="Complete Finance Step 1"
        next={{ to: "/fin/2", label: "Continue to Finance Step 2" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}

export function FinChecklistScreen({
  view,
  busy,
  done,
  step,
  items,
  selected,
  onChange,
  onComplete,
}: StepProps & {
  step: 2 | 3;
  items: Option[];
  selected: string[];
  onChange: (keys: string[]) => void;
}) {
  const copy =
    step === 2
      ? {
          kicker: "Finance Step 2 · Get Your ID Documents Ready",
          title: "Have these ready for your provider.",
          sub: "When you're referred, your provider will verify who you are. Getting these ready now means no delays later. Check off each one once you have it.",
        }
      : {
          kicker: "Finance Step 3 · Get Your Money Records Ready",
          title: "Gather what your provider's checks look at.",
          sub: "Providers decide from your records, not promises. Gather these now so your checks go quickly.",
        };
  const required = items.filter((item) => !item.optional);
  const got = required.filter((item) => selected.includes(item.k)).length;
  return (
    <div>
      <ScreenHeader {...copy} />
      <Guide guideKey={step === 2 ? "fin2" : "fin3"} done={done} />
      {NO_DATA}
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <OptionTile
            key={item.k}
            selected={selected.includes(item.k)}
            title={
              <>
                {item.t}
                {item.optional ? (
                  <span className="text-[12px] font-normal text-text-muted"> (optional)</span>
                ) : null}
              </>
            }
            detail={item.d}
            onToggle={() => onChange(toggle(selected, item.k))}
          />
        ))}
      </div>
      <p className="mt-2.5 text-[13px] text-text-muted">
        {got} of {required.length} ready
      </p>
      <GateFooter
        gate={gateOf(view, step)}
        done={done}
        label={`Complete Finance Step ${step}`}
        next={{ to: `/fin/${step + 1}`, label: `Continue to Finance Step ${step + 1}` }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}

export function FinReadinessScreen({
  view,
  busy,
  done,
  questions,
  onAnswer,
  onComplete,
}: StepProps & { questions: HygieneQuestion[]; onAnswer: (key: string, yes: boolean) => void }) {
  const answers = hygieneAnswers(view);
  const tier = view.liveTier;
  const colour =
    tier === "Ready" ? "text-positive" : tier === "Nearly Ready" ? "text-kicker" : "text-negative";
  return (
    <div>
      <ScreenHeader
        kicker="Finance Step 4 · Readiness Check"
        title="How ready are you for your provider's checks?"
        sub="Four yes-or-no questions. Your answers stay private to the finance team and only produce a readiness level. Your provider runs its own checks when you're referred."
      />
      <Guide guideKey="fin4" done={done} />
      {questions.map((q) => (
        <Section key={q.k} title={q.q}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <OptionTile
              selected={answers[q.k] === true}
              title={q.yes}
              onToggle={() => onAnswer(q.k, true)}
            />
            <OptionTile
              selected={answers[q.k] === false}
              title={q.no}
              onToggle={() => onAnswer(q.k, false)}
            />
          </div>
        </Section>
      ))}
      {tier ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <small className="text-[11px] font-bold uppercase text-text-muted">Your readiness</small>
          <div className={`text-[26px] font-extrabold ${colour}`}>{tier}</div>
          {view.liveFlags.length ? (
            <ul className="m-0 mt-2 list-none p-0 text-[13.5px]">
              {questions
                .filter((q) => view.liveFlags.includes(q.k))
                .map((q) => (
                  <li key={q.k} className="mb-1.5">
                    <b className="text-negative">!</b> <b>{q.q}</b>{" "}
                    <span className="text-text-muted">: {q.fix}</span>
                  </li>
                ))}
            </ul>
          ) : (
            <p className="m-0 mt-1.5 text-[13px]">
              Nothing flagged. Keep your repayments and inflows steady until your referral.
            </p>
          )}
        </div>
      ) : null}
      {tier ? (
        <Banner tone="note">
          {tier === "Needs Work"
            ? "You can only be referred once you're at least Nearly Ready. Fix the flagged items, then update your answers here any time before your handover."
            : "You can update your answers any time before your handover if things change."}
        </Banner>
      ) : null}
      <GateFooter
        gate={gateOf(view, 4)}
        done={done}
        label="Save my result"
        next={{ to: "/fin/5", label: "Continue to Finance Step 5" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}

export function FinKeepGoingScreen({
  view,
  busy,
  done,
  gigStep5Done,
  gigStep5Open,
  onLms,
  onComplete,
}: StepProps & { gigStep5Done: boolean; gigStep5Open: boolean; onLms: (done: boolean) => void }) {
  return (
    <div>
      <ScreenHeader
        kicker="Finance Step 5 · Keep Going"
        title="Stay on the pathway."
        sub="You're referred to your chosen provider when you complete the Growth Pathway. This step completes when you've finished gig Step 5 and your LMS modules up to Step 5."
      />
      <Guide guideKey="fin5" done={done} />
      <Section
        title="Gig Step 5: Run It"
        hint="Manage a live client and build your reputation."
        status={<Pill ok={gigStep5Done} noText="Not yet" />}
      >
        {!gigStep5Done && gigStep5Open ? (
          <Link to="/gig/5" className="font-bold text-accent underline">
            Go to Step 5 →
          </Link>
        ) : null}
      </Section>
      <OptionTile
        selected={view.record.lmsDone}
        title="I've completed my Growth Pathway LMS modules up to Step 5"
        detail="Checked against the LMS record during reconciliation"
        onToggle={() => onLms(!view.record.lmsDone)}
      />
      <GateFooter
        gate={gateOf(view, 5)}
        done={done}
        label="Complete Finance Step 5"
        next={{ to: "/fin/6", label: "Continue to Finance Step 6" }}
        busy={busy}
        onComplete={onComplete}
      />
    </div>
  );
}

/** Finance Step 6. Referral is an explicit action now: the mock recorded it just by opening the page. */
export function FinHandoverScreen({ view, busy, done, onComplete }: StepProps) {
  const gate = gateOf(view, 6);
  return (
    <div>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
        Finance Step 6 · Handover to Your Provider
      </div>
      <div className="mb-4">
        <div className="text-[12px] font-bold uppercase text-text-muted">Status</div>
        <h2 className="text-[26px] text-accent">
          {view.referred ? "Referred to Your Provider" : "Not Referred Yet"}
        </h2>
        <p className="m-0 text-text-muted">
          {view.referred
            ? "You've completed the pathway and you're ready. The finance team has referred you to the partner provider for the services you chose."
            : "You are referred to your provider once everything below is complete."}
        </p>
      </div>
      <Guide guideKey="fin6" done={done} />
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[14px]">
        {gate.items.map((item) => (
          <li key={item.label}>
            <b className={item.done ? "text-positive" : "text-negative"}>{item.done ? "✓" : "×"}</b>{" "}
            {item.label}
            {item.detail ? <span className="text-text-muted"> ({item.detail})</span> : null}
          </li>
        ))}
      </ul>
      {view.referred ? (
        <>
          <Banner tone="note">
            <b>What happens next:</b> your provider will contact you to collect the ID documents and
            money records you prepared in Finance Steps 2 and 3, and run its own checks. The final
            decision and terms are the provider's.
          </Banner>
          <div className="mt-4">
            <BackButton to="/hub" label="Back to my steps" />
          </div>
        </>
      ) : (
        <GateFooter
          gate={gate}
          done={done}
          label="Request my referral"
          busy={busy}
          onComplete={onComplete}
        />
      )}
    </div>
  );
}
